import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import * as FileSaver from 'file-saver';
import * as moment from 'moment';
import swal from 'sweetalert2';
import { environment } from '../../../environments/environment';
import { UiService } from '../ui/ui.service';
import { UserService } from '../user/user.service';

@Injectable()
export class HelperService {
  public hideStudentOfClasses = [];

  public capstoneBranches = [2];
  public currentBranchId = null;
  public diplomaPaperText = new Proxy(
    {
      singular: null,
      plural: null,
      toString: () => { },
    },
    {
      get: (target, prop) => {
        const toString = () => {
          this.getDiplomaPaperText(String(prop), this.currentBranchId);
        };
        return prop === 'toString' ? toString : toString();
      },
    }
  );

  constructor(
    private router: Router,
    private user: UserService,
    private location: Location,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private ui: UiService
  ) { }

  get baseRoute() {
    if (this.user.role === 'exchange') {
      return '/exchange-student';
    } else {
      return '/';
    }
  }

  static textareaToHTML(str) {
    return str.replace(/\r?\n/g, '<br />');
  }

  static shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }

  static titleCase(value) {
    return value
      .split(/\s+/)
      .map(
        (str) =>
          (str && str[0] && String(str[0]).toUpperCase()) +
          (str && String(str).slice(1).toLowerCase())
      )
      .join(' ');
  }

  getDiplomaPaperText(number = 'singular', branchId = undefined) {
    branchId = branchId || this.user.userBranchId;
    if (!branchId) {
      return '';
    }
    const isCapstone = this.capstoneBranches.some(
      (branch) => branch == branchId
    );
    if (isCapstone) {
      return number === 'plural' ? 'Capstone Projects' : 'Capstone Project';
    }
    return number === 'plural' ? 'Diploma Papers' : 'Diploma Paper';
  }

  navigateBack() {
    this.location.back();
  }

  getProfileImageUrl(image) {
    return image
      ? environment.basePath + '/' + image
      : '../assets/img/icon-no-image.png';
  }

  getSafeFileUrl(file) {
    return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
  }

  getDownloadablePath(file) {
    const filename = '/' + file.enc_name + '.' + file.extension;
    const filepath = file.tmp_path
      ? file.tmp_path + filename
      : file.path + filename;
    return environment.basePath + '/' + filepath;
  }

  fromRoman(str): number {
    if (!str) {
      return 0;
    }
    let result = 0;
    // the result is now a number, not a string
    const decimal = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
    const roman = [
      'M',
      'CM',
      'D',
      'CD',
      'C',
      'XC',
      'L',
      'XL',
      'X',
      'IX',
      'V',
      'IV',
      'I',
    ];
    decimal.forEach((value, i) => {
      while (str.indexOf(roman[i]) === 0) {
        // checking for the first characters in the string
        result += value;
        // adding the decimal value to our result counter
        str = str.replace(roman[i], '');
        // remove the matched Roman letter from the beginning
      }
    });
    return result;
  }

  validateDate(date) {
    return date
      ? date instanceof Date
        ? date
        : new Date(date.replace(/-/g, '/'))
      : null;
  }

  handleError(err, type) {
    if (err) {
      if (Object.keys(err).length) {
        swal(this.toTitleCase(type), err[Object.keys(err)[0]], type);
      }
    }
    return true;
  }

  toTitleCase(str: string) {
    return str[0].toUpperCase() + str.slice(1);
  }

  parseNotification(notification, data, relation: any = {}) {
    const { notification_constant } = notification;
    const { title } = notification_constant.constant;
    const commonFields = {
      type: notification_constant.key,
      data: {
        id: notification.fk,
      },
      time: this.getTimeElapsed(moment(notification.created_at)),
    };
    const reducerObj = {
      peer_evaluation: () => ({
        ...commonFields,
        title: 'Peer Evaluation',
        body: relation['real_life_project']
          ? relation['real_life_project'].name
          : data.rlp,
      }),
      course_evaluation: () => ({
        ...commonFields,
        title: 'Course Evaluation',
        body: relation['name'] || data.name,
        // title: title.replace(/#course/g, data.name),
      }),
      rlp_evaluation: () => ({
        ...commonFields,
        title: 'Live Project Evaluation',
        body: relation['name'] || data.name,
        // title: title.replace(/#course/g, data.name),
      }),
      elective_and_seminar_evaluation: () => {
        const type = relation.type == 1 ? 'Elective' : 'Field Seminar';
        return {
          ...commonFields,
          title: type + ' Evaluation',
          body: relation['course_title'] || data.name,
          // title: title.replace(/#course/g, data.name),
        };
      },
    };
    return reducerObj[notification_constant.key]
      ? reducerObj[notification_constant.key]()
      : '';
  }

  getTimeElapsed(from: moment.Moment) {
    return moment.duration(moment().diff(from)).humanize();
  }

  isHtmlEmpty(html) {
    if (html) {
      for (let i = 0; i < html.length; i++) {
        if (html[i] === '<') {
          while (html[++i] !== '>' && i < html.length) { }
        } else {
          return false;
        }
      }
    }
    return true;
  }

  downloadFile(file) {
    console.log(file);
    const name = '/' + file.enc_name + '.' + file.extension;
    const pathToFile = file.tmp_path
      ? file.tmp_path + name
      : file.path
        ? file.path + name
        : file.enc_name;
    console.log(pathToFile);
    this.ui.loader.show();
    this.http
      .post('/download', { pathToFile }, { responseType: 'blob' })
      .subscribe(
        (res: any) => {
          this.ui.loader.hide();
          const downloadableName = file.title
            ? file.title + '.' + file.extension
            : file.name;
          FileSaver.saveAs(res, downloadableName);
        },
        (err) => {
          this.ui.loader.hide();
          console.log(err);
        }
      );
  }

  getLinkUrl(link) {
    return link.url && link.url.startsWith('http')
      ? link.url
      : 'http://' + link.url;
  }

  deepClone(value) {
    switch (typeof value) {
      case 'object':
        if (Array.isArray(value)) {
          return [...value.map((item) => this.deepClone(item))];
        } else {
          return Object.keys(value).reduce((newObj, key) => {
            return {
              ...newObj,
              [key]: this.deepClone(value[key]),
            };
          }, {});
        }
      default:
        return value;
    }
  }

  gradeToNum(grade) {
    const table = {
      A: 96,
      B: 90,
      C: 80,
      D: 60,
      E: 50,
      F: 49,
    };

    if (grade === '') return 0;
    return table[String(grade).toUpperCase()];
  }

  numToGrade(num) {
    const table = {
      0: '',
      96: 'A',
      90: 'B',
      80: 'C',
      60: 'D',
      50: 'E',
      49: 'F',
    };

    if (table[num])
      return table[num];
    else
      return table[0];
  }

  overallPercentage(count, total) {
    const percentage = (count / total * 100).toFixed(2);

    return percentage;
  }

  percentageData(data) {
    const newData = data.map((exam) => {
      return {
        ...exam,
        grade_a_percentage: exam.a_count && exam.total_count ? this.overallPercentage(exam.a_count, exam.total_count) : 0.00,
        grade_b_percentage: exam.b_count && exam.total_count ? this.overallPercentage(exam.b_count, exam.total_count) : 0.00,
        grade_c_percentage: exam.c_count && exam.total_count ? this.overallPercentage(exam.c_count, exam.total_count) : 0.00,
        grade_d_percentage: exam.d_count && exam.total_count ? this.overallPercentage(exam.d_count, exam.total_count) : 0.00,
        grade_e_percentage: exam.e_count && exam.total_count ? this.overallPercentage(exam.e_count, exam.total_count) : 0.00,
        grade_f_percentage: exam.f_count && exam.total_count ? this.overallPercentage(exam.f_count, exam.total_count) : 0.00,
      }
    })

    return newData;
  }

}



// WEBPACK FOOTER //
// G:/UTL/Student/Student-ANG/src/app/services/helper/helper.service.ts